import {
  Checkbox,
  DefaultButton,
  IconButton,
  Label,
  Modal,
  PrimaryButton,
  Stack,
  StackItem,
  TextField,
  Text,
  IComboBoxOption,
  IStackItemStyles,
} from '@fluentui/react';
import { getTheme } from '@fluentui/react/lib/Styling';
import React, { FormEvent, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import {
  ComboboxWithFilter,
  DatePickerNL,
  TimePicker,
} from '../../../components/parts';
import {
  cancelIcon,
  convertSuppliersToComboBoxOptions,
  iconButtonStyles,
  IConstructionSite,
  ISupplier,
  modalContentStyles,
  stackTokens5,
  stackTokens50,
} from '../../../utils';
import { IPurchaseOrderState } from '../PurchaseOrder';

const theme = getTheme();

const styleTitle: IStackItemStyles = {
  root: {
    background: theme.palette.neutralLight,
    padding: '10px',
    marginTop: '15px',
    marginBottom: '15px',
    color: 'black',
    fontSize: '16px',
    fontWeight: 'bold',
    h3: {
      margin: 0,
    },
  },
};

type PurchaseOrderFooterProps = {
  order: IPurchaseOrderState;
  constructionSite?: IConstructionSite;
  setPurchaseOrderState: React.Dispatch<
    React.SetStateAction<IPurchaseOrderState>
  >;
  suppliers?: ISupplier[];
};

const PurchaseOrderFooter = ({
  order,
  setPurchaseOrderState,
  constructionSite,
  suppliers,
}: PurchaseOrderFooterProps) => {
  // Constants

  const [isModalDeliveryOpen, setIsModalDeliveryOpen] = useState(false);

  // Event handlers
  const onChangeDate = (newDate: Date) => {
    const currentOrder = { ...order.purchaseOrder };

    let currentDate;
    let hour;
    let minutes;
    if (order && order.purchaseOrder && order.purchaseOrder.delivery_date) {
      currentDate = moment(order.purchaseOrder.delivery_date);
      hour = currentDate.format('HH');
      minutes = currentDate.format('mm');
    }

    const newParsedDate = moment(newDate);

    if (currentDate && hour && minutes) {
      newParsedDate
        .set('h', parseFloat(hour))
        .set('m', parseFloat(minutes))
        .add(1, 's');
    }

    if (currentOrder) {
      currentOrder.delivery_date = newParsedDate.toISOString();
      setPurchaseOrderState((prevState) => ({
        ...prevState,
        purchaseOrder: currentOrder,
      }));
    }
  };

  const onChangeDeliverToConstructionSite = (
    ev?: FormEvent<HTMLInputElement | HTMLElement> | undefined,
    checked?: boolean | undefined,
  ) => {
    if (checked !== undefined) {
      const currentOrder = { ...order.purchaseOrder };
      if (currentOrder) {
        currentOrder.delivery_to_other_supplier = !checked;
        console.log(currentOrder);
        setPurchaseOrderState((prevState) => ({
          ...prevState,
          purchaseOrder: currentOrder,
        }));
      }
    }
  };

  const setDeliverySupplier = (deliverySupplier: ISupplier) => {
    const currentOrder = { ...order.purchaseOrder };
    if (currentOrder) {
      currentOrder.deliver_to_supplier = deliverySupplier;
      setPurchaseOrderState((prevState) => ({
        ...prevState,
        purchaseOrder: currentOrder,
      }));
    }
  };

  const onChangeRemarks = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const currentOrder = { ...order.purchaseOrder };
    if (currentOrder) {
      currentOrder.remarks = newValue;
      setPurchaseOrderState((prevState) => ({
        ...prevState,
        purchaseOrder: currentOrder,
      }));
    }
  };

  const toggleModalDelivery = () => {
    setIsModalDeliveryOpen(!isModalDeliveryOpen);
  };

  // Functions
  const openModal = () => {
    toggleModalDelivery();
  };

  const closeModal = () => {
    toggleModalDelivery();
  };

  const updateIsOnCall = (isOnCall: boolean) => {
    const currentOrder = { ...order.purchaseOrder };
    if (currentOrder) {
      currentOrder.is_on_call = !!isOnCall;
      setPurchaseOrderState((prevState) => ({
        ...prevState,
        purchaseOrder: currentOrder,
      }));
    }
  };

  // Display functions
  const getDeliveryAddressLabel = () => {
    if (
      order
      && order.purchaseOrder
      && order.purchaseOrder.delivery_to_other_supplier === true
    ) {
      if (order.purchaseOrder.deliver_to_supplier) {
        return (
          <Text style={{ marginTop: 0, marginBottom: 3 }}>
            {order.purchaseOrder.deliver_to_supplier.name}
            {' - '}
            {order.purchaseOrder.deliver_to_supplier.address}
            {', '}
            {order.purchaseOrder.deliver_to_supplier.zip_code}{' '}
            {order.purchaseOrder.deliver_to_supplier.city}
          </Text>
        );
      }
    } else {
      if (order && order.purchaseOrder.construction_site) {
        return (
          <Text style={{ marginTop: 0, marginBottom: 3 }}>
            {/* {order.construction_site.name} */}
            {'Levering op de werf: '}
            {order.purchaseOrder.construction_site.address}
            {', '}
            {order.purchaseOrder.construction_site.zip_code}{' '}
            {order.purchaseOrder.construction_site.city}{' '}
            {/* {order.construction_site.comments} */}
          </Text>
        );
      }
      if (constructionSite) {
        return (
          <Text style={{ marginTop: 0, marginBottom: 3 }}>
            {/* {constructionSite.name} */}
            {'Levering op de werf: '}
            {constructionSite.address}
            {', '}
            {constructionSite.zip_code} {constructionSite.city}{' '}
            {/* {constructionSite.comments} */}
          </Text>
        );
      }
    }
  };

  const callBackTimePicker = (value: string) => {
    setPurchaseOrderState((prevState) => {
      const currentOrder = _.cloneDeep(prevState.purchaseOrder);
      currentOrder.delivery_date = value;
      return {
        ...prevState,
        purchaseOrder: currentOrder,
      };
    });
  };

  const changeDeliveryDuringDay = (
    ev?: FormEvent<HTMLInputElement | HTMLElement> | undefined,
    checked?: boolean | undefined,
  ) => {
    const newValue = checked !== undefined ? checked : false;
    const currentOrder = { ...order.purchaseOrder };

    if (currentOrder) {
      currentOrder.no_delivery_hour = newValue;
      setPurchaseOrderState((prevState) => ({
        ...prevState,
        purchaseOrder: currentOrder,
      }));
    }
  };

  const formatDeliveryDate = (value: string) => moment(value).toISOString();
  return (
    <div>
      <Stack
        // onClick={() => toggleItem(id)}
        styles={styleTitle}
        horizontal
        horizontalAlign='space-between'
      >
        <Stack.Item>
          <h3>Levering</h3>
        </Stack.Item>
      </Stack>
      <Stack tokens={stackTokens5}>
        <Stack horizontal tokens={stackTokens50}>
          <Stack tokens={stackTokens5}>
            <Label>LEVERINGSADRES:</Label>
            {getDeliveryAddressLabel()}

            <StackItem>
              <DefaultButton text='Wijzig' onClick={openModal} />
            </StackItem>
          </Stack>

          {order && order.purchaseOrder && (
            <Stack tokens={stackTokens5}>
              <Label>OP AFROEP?:</Label>
              <Checkbox
                label='Op afroep'
                checked={order.purchaseOrder.is_on_call}
                defaultChecked={
                  order && order.purchaseOrder && order.purchaseOrder.is_on_call
                }
                onChange={(
                  ev?:
                    | React.FormEvent<HTMLInputElement | HTMLElement>
                    | undefined,
                  checked?: boolean | undefined,
                ) => {
                  updateIsOnCall(checked !== undefined ? checked : false);
                }}
              />
            </Stack>
          )}

          <Stack tokens={stackTokens5}>
            {order && order.purchaseOrder && order.purchaseOrder.is_on_call ? (
              <Label>RICHTDATUM:</Label>
            ) : (
              <Label>LEVERINGSDATUM:</Label>
            )}

            <DatePickerNL
              updateParent={onChangeDate}
              value={
                order
                && order.purchaseOrder
                && order.purchaseOrder.delivery_date
                  ? formatDeliveryDate(order.purchaseOrder.delivery_date)
                  : undefined
              }
            />
          </Stack>

          <Stack tokens={stackTokens5}>
            {order && order.purchaseOrder && !order.purchaseOrder.is_on_call && (
              <Stack>
                <Label>TIJDSTIP:</Label>
                <StackItem tokens={stackTokens50}>
                  <Checkbox
                    label='In de loop van de dag'
                    checked={
                      order
                      && order.purchaseOrder
                      && order.purchaseOrder.no_delivery_hour
                    }
                    onChange={changeDeliveryDuringDay}
                  />
                  {order
                    && order.purchaseOrder
                    && !order.purchaseOrder.no_delivery_hour && (
                      <StackItem style={{ marginTop: 15 }}>
                        <TimePicker
                          callback={callBackTimePicker}
                          currentDate={order.purchaseOrder.delivery_date}
                          divider=':'
                        />
                      </StackItem>
                  )}
                </StackItem>
              </Stack>
            )}
          </Stack>
        </Stack>

        <TextField
          label='OPMERKINGEN:'
          value={
            order && order.purchaseOrder && order.purchaseOrder.remarks
              ? order.purchaseOrder.remarks
              : undefined
          }
          multiline
          styles={{ fieldGroup: { width: 1018 } }}
          rows={6}
          onChange={onChangeRemarks}
        />

        {/* {getContactDetailsDelivery()} */}
      </Stack>

      <Modal
        titleAriaId='Wijzig leveradres'
        isOpen={isModalDeliveryOpen}
        onDismiss={toggleModalDelivery}
        isModeless
        containerClassName={modalContentStyles.container}
      >
        <div className={modalContentStyles.header}>
          <span>Wijzig leveradres</span>
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel='Sluit venster'
            onClick={toggleModalDelivery}
          />
        </div>

        <div className={modalContentStyles.body}>
          <Stack tokens={stackTokens5}>
            <Stack.Item>
              <Checkbox
                label='Levering op de werf'
                checked={
                  order
                  && order.purchaseOrder
                  && !order.purchaseOrder.delivery_to_other_supplier
                }
                onChange={onChangeDeliverToConstructionSite}
              />

              {order
                && order.purchaseOrder
                && order.purchaseOrder.delivery_to_other_supplier
                && suppliers && (
                  <ComboboxWithFilter
                    allowFreeForm
                    label='Leverancier'
                    callBack={(selection: IComboBoxOption[]) => {
                      const ids = selection.map((option) => option.key);
                      if (suppliers.length > 0) {
                        const selectedSupplier = suppliers.filter((supplier) => {
                          if (supplier && ids.includes(supplier.id)) return true;
                          return false;
                        })[0]; // can only select one otherwise leave [0] out @TP

                        if (selectedSupplier) {
                          setDeliverySupplier(selectedSupplier);
                        }
                      }
                      // setSelectedSupplier(0);
                    }}
                    options={
                      suppliers && Array.isArray(suppliers)
                        ? convertSuppliersToComboBoxOptions(suppliers)
                        : []
                    }
                    multiline={false}
                    value={
                      order
                      && order.purchaseOrder
                      && order.purchaseOrder.deliver_to_supplier
                        ? [order.purchaseOrder.deliver_to_supplier.id]
                        : undefined
                    }
                  />
              )}
            </Stack.Item>
            <Stack.Item>
              <Stack horizontalAlign='end' style={{ marginTop: 10 }}>
                <PrimaryButton text='Sluiten' onClick={closeModal} />
              </Stack>
            </Stack.Item>
          </Stack>
        </div>
      </Modal>
    </div>
  );
};

export default PurchaseOrderFooter;
